import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import styles from './EditTeamDialog.module.scss'

import { isApiError } from '@/api'
import type { EnterpriseMemberRowResponse, TeamResponse } from '@/api/generated'
import { listMembers, moveMemberToTeam } from '@/api/members'
import { updateTeam } from '@/api/team'
import { getTeamMembersTable } from '@/api/teamMembersTable'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormErrorBanner from '@/components/FormErrorBanner'
import Input from '@/components/Input'
import { enterpriseTeamsQueryKey } from '@/hooks/useEnterpriseTeams'
import MemberMultiSelect, { MemberOption } from '@/components/MemberMultiSelect'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import {
  MY_ACTIVE_COST_CENTER_QUERY_KEY,
  pickDefaultDestinationTeamId,
  TEAM_MEMBERS_PAGE_SIZE,
  TEAM_MEMBERS_TABLE_QUERY_KEY,
} from '@/pages/app/Team/Department/utils'

interface EditTeamDialogProps {
  isOpen: boolean
  team: TeamResponse | null
  allTeams: TeamResponse[]
  enterpriseId: string
  onClose: () => void
}

const EditTeamDialog = ({ isOpen, team, allTeams, enterpriseId, onClose }: EditTeamDialogProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const nameId = useId()

  const [name, setName] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  // For users we're removing (originally in this team, now unchecked), which
  // department should they land in?
  const [removalDestinations, setRemovalDestinations] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const { data: teamMembersData, isLoading: isLoadingTeamMembers } = useQuery({
    queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY, team?.id ?? '', { sortBy: 'email', sortDir: 'asc' }],
    queryFn: ({ signal }) =>
      getTeamMembersTable({
        teamId: team!.id,
        limit: TEAM_MEMBERS_PAGE_SIZE,
        sortBy: 'email',
        sortDir: 'asc',
        signal,
      }).then((r) => r.data),
    enabled: isOpen && !!team,
  })

  const { data: allMembersResp, isLoading: isLoadingAllMembers } = useQuery({
    queryKey: ['enterprise-members'],
    queryFn: () => listMembers(),
    enabled: isOpen,
  })

  const currentMembers: EnterpriseMemberRowResponse[] = useMemo(() => teamMembersData?.rows ?? [], [teamMembersData])

  const currentMemberIds = useMemo(() => new Set(currentMembers.map((m) => m.userId)), [currentMembers])

  const allMembers: MemberOption[] = useMemo(() => {
    return (allMembersResp?.data?.members ?? []).map((m) => ({
      userId: m.userId,
      email: m.email ?? '',
      firstName: m.firstName ?? '',
      lastName: m.lastName ?? '',
    }))
  }, [allMembersResp])

  const otherTeamOptions = useMemo(() => {
    if (!team) return []
    return allTeams.filter((t) => t.id !== team.id).map((t) => ({ value: t.id, label: t.name }))
  }, [allTeams, team])

  // Initialize the form exactly once per (team, open) cycle. Re-running this
  // when ``currentMembers`` re-references (background refetch, query
  // invalidation) would clobber the admin's in-progress edits. The ref keys
  // off team id + whether we've already initialized for this open cycle.
  const initializedForTeamRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen) {
      initializedForTeamRef.current = null
      return
    }
    if (!team) return
    if (initializedForTeamRef.current === team.id) return
    // Wait for the team members query to land before locking in the initial
    // selection — otherwise we'd seed with an empty list on first paint.
    if (isLoadingTeamMembers) return
    initializedForTeamRef.current = team.id
    setName(team.name)
    setSelectedUserIds(currentMembers.map((m) => m.userId))
    setRemovalDestinations({})
    setFormError(null)
  }, [isOpen, team, currentMembers, isLoadingTeamMembers])

  // Members who started in the team but are now unchecked — these need a
  // destination department.
  const removedUserIds = useMemo(() => {
    const selected = new Set(selectedUserIds)
    return Array.from(currentMemberIds).filter((id) => !selected.has(id))
  }, [currentMemberIds, selectedUserIds])

  // Whenever the removal list changes, ensure every removed user has a default
  // destination (General team, falling back as appropriate).
  useEffect(() => {
    if (!team) return
    const defaultId = pickDefaultDestinationTeamId(allTeams, team)
    if (!defaultId) return
    setRemovalDestinations((prev) => {
      const next: Record<string, string> = {}
      for (const uid of removedUserIds) next[uid] = prev[uid] ?? defaultId
      return next
    })
  }, [removedUserIds, allTeams, team])

  // Members being newly added to this team (selected, but not originally a
  // member).
  const addedUserIds = useMemo(() => {
    return selectedUserIds.filter((id) => !currentMemberIds.has(id))
  }, [selectedUserIds, currentMemberIds])

  // Lookup helpers for the removed-members section.
  const memberById = useMemo(() => {
    const m = new Map<string, EnterpriseMemberRowResponse>()
    for (const row of currentMembers) m.set(row.userId, row)
    return m
  }, [currentMembers])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!team) throw new Error('No team selected')
      const trimmedName = name.trim()
      if (!trimmedName) throw new Error('Department name is required')

      // Pre-validate destinations before firing any requests so a bad form state
      // doesn't half-apply.
      for (const userId of removedUserIds) {
        if (!removalDestinations[userId]) throw new Error('Pick a destination for each removed member')
      }

      // 1) Rename if changed. Must precede member moves so the title is consistent
      // with the new membership in the success toast.
      if (trimmedName !== team.name) {
        await updateTeam(team.id, { name: trimmedName })
      }

      // 2) Adds and removes are independent — fan out and report any failures.
      const moves: Promise<unknown>[] = [
        ...addedUserIds.map((userId) => moveMemberToTeam(team.id, { user_id: userId })),
        ...removedUserIds.map((userId) => moveMemberToTeam(removalDestinations[userId], { user_id: userId })),
      ]
      const results = await Promise.allSettled(moves)
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failures.length > 0) {
        const first = failures[0].reason
        const detail = isApiError(first) ? (first.detail ?? first.message) : 'Some member moves failed.'
        throw {
          message: detail,
          status: isApiError(first) ? first.status : 500,
          detail:
            failures.length === 1 ? detail : `${failures.length} of ${moves.length} member moves failed. ${detail}`,
        }
      }
    },
    onSuccess: () => {
      updateToast('Department updated', 'success')
      queryClient.invalidateQueries({ queryKey: enterpriseTeamsQueryKey(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['enterprise-members'] })
      // Caller's CC-wide member list (and each member's team_id /
      // is_effective_team_admin) just shifted — without this, the next
      // Create-Department open evaluates `isSoleAdminWithOthers` against the
      // pre-move snapshot and the successor-admin field fails to render.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
      onClose()
    },
    onError: (err: unknown) => {
      setFormError(
        isApiError(err)
          ? (err.detail ?? 'Failed to update department. Please try again.')
          : 'Failed to update department. Please try again.'
      )
    },
  })

  if (!team) return null

  const isLoading = isLoadingTeamMembers || isLoadingAllMembers
  const ready = removedUserIds.every((id) => Boolean(removalDestinations[id]))

  return (
    <DialogWrapper
      isOpen={isOpen}
      title="Edit department"
      close={() => {
        if (mutation.isPending) return
        onClose()
      }}
      minWidth={560}
    >
      <div className={styles.body}>
        <div className={styles.field}>
          <label htmlFor={nameId} className={styles.label}>
            Department name
          </label>
          <Input
            id={nameId}
            value={name}
            onInput={setName}
            type="text"
            placeholder="e.g., Finance, Product, Marketing…"
            isDisabled={mutation.isPending}
          />
        </div>

        {isLoading ? (
          <p className={styles.loading}>Loading members…</p>
        ) : (
          <MemberMultiSelect
            members={allMembers}
            selectedUserIds={selectedUserIds}
            onChange={setSelectedUserIds}
            isDisabled={mutation.isPending}
          />
        )}

        {removedUserIds.length > 0 && (
          <div className={styles.removalSection}>
            <div className={styles.label}>Move removed members to</div>
            <ul className={styles.removalList}>
              {removedUserIds.map((uid) => {
                const m = memberById.get(uid)
                const label = m?.email ?? uid
                return (
                  <li key={uid} className={styles.removalRow}>
                    <Select
                      value={removalDestinations[uid] ?? ''}
                      options={otherTeamOptions}
                      onChange={(val) => setRemovalDestinations((prev) => ({ ...prev, [uid]: val }))}
                      placeholder="Pick a department"
                      isDisabled={mutation.isPending}
                    />
                    <span className={styles.removalLabel}>{label}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {formError && <FormErrorBanner message={formError} />}

        <div className={styles.actions}>
          <Button title="Cancel" onClick={onClose} type="light" isDisabled={mutation.isPending} />
          <Button
            title={mutation.isPending ? 'Saving…' : 'Save'}
            onClick={() => mutation.mutate()}
            type="dark"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending || isLoading || !ready}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default EditTeamDialog
